import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useDeliveryManagement } from "./useHook";
import InStationZoneModal from "../../Components/Modals/InStationZoneModal/InStationZoneModal";
import { MdDelete, MdEdit } from "react-icons/md";
import Loader from "../../Utils/loader";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";

function InStationZonePage() {
    const { getInStationZones, addInStationZone, updateInStationZone, deleteInStationZone } = useDeliveryManagement();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [zones, setZones] = useState([]);
    const [editZone, setEditZone] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [pagination, setPagination] = useState({
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
        perPage: 10
    });
    const [offset, setOffset] = useState(0);
    const [limit] = useState(10);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => {
        setIsModalOpen(false);
        setEditZone(null);
    };

    const fetchZones = async () => {
        try {
            setIsLoading(true);
            const response = await getInStationZones(setZones, offset, limit);

            if (response?.pagination) {
                setPagination({
                    totalRecords: response.pagination.totalRecords,
                    totalPages: response.pagination.totalPages,
                    currentPage: response.pagination.currentPage,
                    perPage: response.pagination.perPage
                });
            }
        } catch (error) {
            console.error("Error fetching zones:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchZones();
    }, [offset]);

    const handlePageChange = (newOffset) => {
        setOffset(newOffset);
    };

    const handleAddOrUpdateZone = async (zoneData) => {
        try {
            if (editZone) {
                const response = await updateInStationZone(editZone.id, zoneData);
                if (response) {
                    Swal.fire({
                        icon: "success",
                        title: "Zone Updated",
                        text: "Your zone has been successfully updated!",
                        confirmButtonText: "OK",
                    });
                }
            } else {
                const response = await addInStationZone(zoneData);
                if (response) {
                    Swal.fire({
                        icon: "success",
                        title: "Zone Added",
                        text: "Your zone has been successfully added!",
                        confirmButtonText: "OK",
                    });
                }
            }
            await fetchZones();
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Failed to Save Zone",
                text: "An error occurred while saving the zone. Please try again.",
                confirmButtonText: "OK",
            });
        }
        closeModal();
    };

    const handleEditZone = (zone) => {
        setEditZone(zone);
        openModal();
    };

    const handleDeleteZone = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                const response = await deleteInStationZone(id);
                if (response) {
                    Swal.fire({
                        icon: "success",
                        title: "Deleted!",
                        text: "The zone has been deleted.",
                        confirmButtonText: "OK",
                    });
                    await fetchZones();
                }
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: "Failed to Delete",
                    text: "An error occurred while deleting the zone. Please try again.",
                    confirmButtonText: "OK",
                });
            }
        }
    };

    if (isLoading) {
        return <Loader message="Loading zones..." />;
    }

    return (
        <>
            <div>
                <div className="bg-gray-100 p-4 flex justify-between">
                    <h2 className="text-2xl font-bold">Manage In Station Zones</h2>
                    <button
                        className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
                        onClick={openModal}
                    >
                        Add In Station Zone
                    </button>
                </div>

                <div className="mt-4">
                    <div className="bg-gray-100">
                        <div className="overflow-x-auto mt-6">
                            <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
                                <thead>
                                    <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
                                        <th className="p-4 text-left">Sr. No</th>
                                        <th className="p-4 text-left">Zone Name</th>
                                        <th className="p-4 text-left">Min Distance</th>
                                        <th className="p-4 text-left">Max Distance</th>
                                        <th className="p-4 text-left">Multiplier</th>
                                        <th className="p-4 text-left">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {zones?.length > 0 ? (
                                        zones?.map((zone, index) => (
                                            <tr
                                                key={zone.id}
                                                className="border-t border-gray-200 hover:bg-gray-50"
                                            >
                                                <td className="p-4">{offset + index + 1}</td>
                                                <td className="p-4">{zone.zone_name}</td>
                                                <td className="p-4">{zone.min_distance}</td>
                                                <td className="p-4">{zone.max_distance}</td>
                                                <td className="p-4">{zone.multiplier}</td>
                                                <td className="px-4 py-2 border-b">
                                                    <div className="flex gap-2">
                                                        <button
                                                            className="bg-primary-dark text-white py-2 px-4 rounded-lg shadow-md flex items-center"
                                                            onClick={() => handleEditZone(zone)}
                                                        >
                                                            <MdEdit />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteZone(zone.id)}
                                                            className="bg-red-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center"
                                                        >
                                                            <MdDelete />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-6 text-gray-500">
                                                No data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Component */}
                        {pagination.totalPages > 1 && (
                            <div className="mt-6">
                                <GlobalPagination
                                    offset={offset}
                                    limit={limit}
                                    total={pagination.totalRecords}
                                    onPageChange={handlePageChange}
                                    className="mt-4"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <InStationZoneModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleAddOrUpdateZone}
                editData={editZone}
            />
        </>
    );
}

export default InStationZonePage;